import React, { useState } from 'react';
import { BriefcaseIcon, TrashIcon, CheckIcon, ClockIcon } from '@heroicons/react/24/outline';
import { PlusIcon } from '@heroicons/react/20/solid';

import { validateBreak, validateBreakCrossMidnight } from '../utils/timeslots';
import { toMin } from '../utils/useWeeklyShiftEditor';
import LocalBarOutlinedIcon from '@mui/icons-material/LocalBarOutlined';





const TimeSlot = ({ onShiftPreview, onCancelEdit, onSaveEdit, shiftPreview, allowHiddenBreaks, onAssignWh, onWeekShiftComponent }) => {
  const [workStartTime, setWorkStartTime] = useState(shiftPreview?.workStartTime || '');
  const [workEndTime, setWorkEndTime] = useState(shiftPreview?.workEndTime || '');
  const [error, setError] = useState('');
  const [pauseTimes, setPauseTimes] = useState(
    (shiftPreview?.breaks || []).map(pause => ({
      ...pause,
      confirmed: true,
      is_visible: pause?.is_visible ?? true,
    }))
  ); // todo: check
  const [creatingPause, setCreatingPause] = useState(false);
  // const [shiftPreview, setShiftPreview] = useState<ShiftPreview | null>(shiftPreviewData);
  const [workTimeError, setWorkTimeError] = useState('');
  const [breakTimeError, setBreakTimeError] = useState('');

  const allowCrossMidnight = onAssignWh === 'assignWhComponent';
  const isNewWeekShift = onWeekShiftComponent === 'weekShiftComponent';

  const isCompleteTime = (value) =>
    typeof value === 'string' && /^\d{2}:\d{2}$/.test(value);

  const isPauseWithinWorkWindow = (workStart, workEnd, pauseStart, pauseEnd) => {
    const ws = toMin(workStart);
    const we = toMin(workEnd);
    const ps = toMin(pauseStart);
    const pe = toMin(pauseEnd);

    // durante digitazione non bloccare con errore prematuro
    if (ws == null || we == null || ps == null || pe == null) return true;

    if (!allowCrossMidnight) {
      if (we <= ws) return true; // errore ordine orari gestito altrove
      return ps >= ws && pe <= we && pe > ps;
    }

    const crossesMidnight = we < ws;
    const workEndAbs = crossesMidnight ? we + 1440 : we;
    const normalize = (value) => (crossesMidnight && value < ws ? value + 1440 : value);

    const psAbs = normalize(ps);
    const peAbs = normalize(pe);

    if (peAbs <= psAbs) return false;

    return psAbs >= ws && peAbs <= workEndAbs;
  };

  const getWorkAndBreakError = (nextWorkStart, nextWorkEnd) => {
    if (!isCompleteTime(nextWorkStart) || !isCompleteTime(nextWorkEnd)) return '';

    const ws = toMin(nextWorkStart);
    const we = toMin(nextWorkEnd);

    if (ws == null || we == null) {
      return 'Orari di lavoro non validi.';
    }

    if (ws === we) {
      setError("L'orario di fine deve essere diverso dall'orario di inizio.");
      return;
    }

    if (!allowCrossMidnight && we <= ws) {
      return "L'orario di fine deve essere successivo all'orario di inizio.";
    }

    const hasPauseOutOfRange = pauseTimes.some((pause) => {
      if (!pause?.start_at || !pause?.end_at) return false;
      if (!isCompleteTime(pause.start_at) || !isCompleteTime(pause.end_at)) return false;

      return !isPauseWithinWorkWindow(
        nextWorkStart,
        nextWorkEnd,
        pause.start_at,
        pause.end_at
      );
    });

    if (hasPauseOutOfRange) {
      return "Uno o più periodi di pausa non sono inclusi nell'orario di lavoro.";
    }

    return '';
  };

  
  const handleCancelClick = () => {
    onCancelEdit();
  };

  const handleWorkStartTimeChange = (e) => {
    let inputValue = e.target.value;
    inputValue = inputValue.replace(/\D/g, '');
    inputValue = inputValue.slice(0, 4);
  
    let formattedTime = inputValue.replace(/(\d{2})(\d{0,2})/, "$1:$2");
  
    if (inputValue.length <= 2 && e.target.value.length < formattedTime.length) {
      formattedTime = formattedTime.replace(":", "");
    }
  
    setWorkStartTime(formattedTime);
    setWorkTimeError(getWorkAndBreakError(formattedTime, workEndTime));
  };

  const handleWorkEndTimeChange = (e) => {
    let inputValue = e.target.value;
  
    inputValue = inputValue.replace(/\D/g, '');
    inputValue = inputValue.slice(0, 4);
  
    let formattedTime = inputValue.replace(/(\d{2})(\d{0,2})/, "$1:$2");
  
    if (inputValue.length <= 2 && e.target.value.length < formattedTime.length) {
      formattedTime = formattedTime.replace(":", "");
    }
  
    setWorkEndTime(formattedTime);
    setWorkTimeError(getWorkAndBreakError(workStartTime, formattedTime));
  };
  

  const handleWorkTimeBlur = (field) => {
    let formattedTime = field === 'start' ? workStartTime : workEndTime;
  
    formattedTime = formattedTime.replace(/\D/g, '');
  
    while (formattedTime.length < 4) {
      formattedTime += '0';
    }
  
    formattedTime = formattedTime.slice(0, 4);
  
    const hours = formattedTime.slice(0, 2);
    const minutes = formattedTime.slice(2);
    formattedTime = `${hours}:${minutes}`;
  
    const isValidTime = /^([01]?[0-9]|2[0-4]):[0-5][0-9]$/.test(formattedTime);
  
    if (!isValidTime || hours == '24' && parseInt(minutes) > 0) {
      if (field === 'start') {
        setWorkStartTime('');
      } else {
        setWorkEndTime('');
      }
      return;
    }
  
    if (field === 'start') {
      setWorkStartTime(formattedTime);
    } else {
      setWorkEndTime(formattedTime);
    }
  };
  
  
  const handleAddPause = () => {
    if (!workStartTime || !workEndTime) {
      setWorkTimeError("Prima di aggiungere una pausa, inserisci l'orario di lavoro.");
      return;
    }
  
    if (!creatingPause && pauseTimes.every(pause => pause.confirmed)) {
      setCreatingPause(true);
      setPauseTimes([...pauseTimes, { start_at: '', end_at: '', confirmed: false, is_visible: true}]);
      setWorkTimeError('');
    } else {
      setCreatingPause(false);
      setPauseTimes(pauseTimes.filter(pause => pause.confirmed));
    }
  };

  const handlePauseStartTimeChange = (index, e) => {
    const updatedPauseTimes = [...pauseTimes];
    let inputValue = e.target.value;
    
    inputValue = inputValue.replace(/\D/g, '');
    inputValue = inputValue.slice(0, 4);
    
    let formattedTime = inputValue.replace(/(\d{2})(\d{0,2})/, "$1:$2");
  
    if (inputValue.length <= 2 && e.target.value.length < formattedTime.length) {
      formattedTime = formattedTime.replace(":", "");
    }
    
    updatedPauseTimes[index].start_at = formattedTime;
    setPauseTimes(updatedPauseTimes);
  };
  
  const handlePauseEndTimeChange = (index, e) => {
    const updatedPauseTimes = [...pauseTimes];
    let inputValue = e.target.value;
    
    inputValue = inputValue.replace(/\D/g, '');
    inputValue = inputValue.slice(0, 4);
    
    let formattedTime = inputValue.replace(/(\d{2})(\d{0,2})/, "$1:$2");
  
    if (inputValue.length <= 2 && e.target.value.length < formattedTime.length) {
      formattedTime = formattedTime.replace(":", "");
    }
    updatedPauseTimes[index].end_at = formattedTime;
    setPauseTimes(updatedPauseTimes);
  };

const handlePauseTimeBlur = (index, field) => {
  const updatedPauseTimes = [...pauseTimes];
  let formattedTime = updatedPauseTimes[index][field];

  if (typeof formattedTime === 'string') {
    formattedTime = formattedTime.replace(/\D/g, '');

    while (formattedTime.length < 4) {
      formattedTime += '0';
    }

    formattedTime = formattedTime.slice(0, 4);

    const hours = formattedTime.slice(0, 2);
    const minutes = formattedTime.slice(2);
    formattedTime = `${hours}:${minutes}`;

    const isValidTime = /^([01]?[0-9]|2[0-4]):[0-5][0-9]$/.test(formattedTime);

    if (!isValidTime || hours == '24' && parseInt(minutes) > 0) {
      updatedPauseTimes[index][field] = '';
      setPauseTimes(updatedPauseTimes);
      return;
    }

    updatedPauseTimes[index][field] = formattedTime;
    setPauseTimes(updatedPauseTimes);
  }
};

  const handleTogglePauseVisible = (idx) => {
    setPauseTimes(prev => {
      const next = [...prev];
      const cur = next[idx] || {};
      next[idx] = { ...cur, is_visible: !(cur.is_visible ?? true) };
      return next;
    });
  };

  const handleConfirmPause = (index) => {
    const updatedPauseTimes = [...pauseTimes];
    const currentPause = updatedPauseTimes[index];

    try {
      if (!currentPause.start_at || !currentPause.end_at) {
        throw new Error('Completa entrambi gli orari di inizio e fine pausa.');
      }

      if (!allowCrossMidnight) {
        validateBreak(workStartTime, workEndTime, currentPause, pauseTimes);
      } else {
        validateBreakCrossMidnight(workStartTime, workEndTime, currentPause, pauseTimes);
      }

      currentPause.confirmed = true;
      setPauseTimes(updatedPauseTimes);
      setCreatingPause(false);
      setBreakTimeError('');
      setError('');
    } catch (error) {
      console.error(error);
      setBreakTimeError(error.message);
      return;
    }
  };

  const handleDeletePause = (index) => {
    const updatedPauseTimes = [...pauseTimes];
    updatedPauseTimes.splice(index, 1);
    setPauseTimes(updatedPauseTimes);
  };

  const handleConfirmClick = () => {
    if (!workStartTime || !workEndTime) {
      setError("Prima di confermare, inserisci l'orario di lavoro.");
      return;
    }

    const ws = toMin(workStartTime);
    const we = toMin(workEndTime);
    if (ws == null || we == null) {
      setError("Orari di lavoro non validi.");
      return;
    }

    if (ws === we) {
      setError("L'orario di fine deve essere diverso dall'orario di inizio.");
      return;
    }

    if (!allowCrossMidnight && we <= ws) {
      setError("L'orario di fine deve essere successivo all'orario di inizio.");
      return;
    }

    for (const pause of pauseTimes) {
      if (!pause.confirmed) {
        setError("Conferma tutte le pause prima di salvare.");
        return;
      }

      const bs = toMin(pause.start_at);
      const be = toMin(pause.end_at);
      if (bs == null || be == null) {
        setBreakTimeError("Orari pausa non validi.");
        return;
      }

      // BLOCCO CHIAVE: pausa dentro al turno (caso NON cross-midnight)
      if (!allowCrossMidnight) {
        if (bs < ws || be > we) {
          setBreakTimeError("Uno o più periodi di pausa non sono inclusi nell'orario di lavoro.");
          return;
        }
      }

      // Validazioni esistenti (overlap / ecc.)
      try {
        if (!allowCrossMidnight) {
          validateBreak(workStartTime, workEndTime, pause, pauseTimes);
        } else {
          validateBreakCrossMidnight(workStartTime, workEndTime, pause, pauseTimes);
        }
      } catch (err) {
        setBreakTimeError(err.message);
        return;
      }
    }

    setError("");
    setBreakTimeError("");

    const crossesMidnight =
      allowCrossMidnight &&
      workStartTime &&
      workEndTime &&
      workEndTime < workStartTime;

    onShiftPreview({
      workStartTime,
      workEndTime,
      breaks: pauseTimes,
      crossesMidnight,
    });
  };

  return (
    <>
      {(
        <div
          className={`mb-0 bg-white ${
            !isNewWeekShift
              ? "border border-solid-2 border-gray-200 shadow-md rounded-md"
              : ""
          }`}
        >
          <div
            className={`grid grid-cols-1 mx-4 ${
              !isNewWeekShift ? "mt-4" : ""
            }`}
          >
            {!isNewWeekShift && (
              <>    
                <label htmlFor="parent" className="text-sm font-medium text-gray-700 m-0 col-span-1 flex items-center">Inserisci Fascia Oraria *</label>
                {!allowCrossMidnight ? ( 
                  <p className='text-xs pt-2 font-medium text-gray-700 m-0 col-span-1 flex items-center'>L'orario disponibile per la giornata è dalle 00:00 alle 24:00</p>
                ) : (
                  <p className='text-xs pt-2 font-medium text-gray-700 m-0 col-span-1 flex items-center'>Puoi assegnare turni notturni, il sistema li dividerà per te</p>
                )}
              </>
            )}
            {error && (
              <div className="grid grid-cols-1 mx-4">
                <p className="text-red-500 text-sm text-center">{error}</p>
              </div>
            )}
            <div className='grid md:grid-cols-6 sm:grid-cols-1 mt-4 justify-between'>
              <div className='md:col-span-2 col-span-1'>
                <div className="mb-3 flex items-center">
                  <label htmlFor="start-time" className="sm:w-32 text-sm font-medium text-gray-700 flex items-center space-x-1 me-4 sm:me-0">
                    <BriefcaseIcon className='h-5 w-5 mr-2' />
                    Dalle ore:
                  </label>
                  <div style={{ position: 'relative', display: 'inline-block', width: 'fit-content' }}>
                    <input 
                      type="text"
                      placeholder='hh:mm' 
                      value={workStartTime} 
                      onInput={handleWorkStartTimeChange}
                      onBlur={() => handleWorkTimeBlur('start')}
                      className="mt-1 p-1 pl-4 border-gray-200 rounded-md focus:ring-indigo-500 focus:border-indigo-500 block w-32 shadow-md sm:text-sm required" 
                      style={{ paddingRight: '2.5rem' }}
                      maxLength={5}
                    />
                    <span style={{ position: 'absolute', top: '50%', right: '8px', transform: 'translateY(-50%)' }}>
                      <ClockIcon className='w-4 h-4' />
                    </span>
                  </div>
                </div>
              </div>
              <div className='col-span-1'></div>
              <div className='md:col-span-2 col-span-1 flex items-center'>
                <label htmlFor="start-time" className="sm:w-32 text-sm font-medium text-gray-700 flex items-center space-x-1 me-6 sm:me-0">
                  <BriefcaseIcon className='h-5 w-5 mr-2' />
                  Alle ore:
                </label>
                <div style={{ position: 'relative', display: 'inline-block', width: 'fit-content' }}>
                  <input 
                    type="text"
                    placeholder='hh:mm' 
                    value={workEndTime} 
                    onInput={handleWorkEndTimeChange} 
                    onBlur={() => handleWorkTimeBlur('end')}
                    className="p-1 pl-4 border-gray-200 rounded-md focus:ring-indigo-500 focus:border-indigo-500 block w-32 shadow-md sm:text-sm required" 
                  />
                  <span style={{ position: 'absolute', top: '50%', right: '8px', transform: 'translateY(-50%)' }}>
                    <ClockIcon className='w-4 h-4' />
                  </span>
                </div>
              </div>
            </div>
          </div>

          {workTimeError && (
            <p className="text-red-500 text-sm justify-center flex pt-8 ">{workTimeError}</p>
          )}

          {breakTimeError && (
            <p className="text-red-500 text-sm justify-center flex pt-8 ">{breakTimeError}</p>
          )}

          <div className='grid md:grid-cols-6 sm:grid-cols-1 mx-4 pt-7 items-center'>
            <div className='col-span-6 flex items-center'>
              <label htmlFor="parent" className="block text-sm font-medium text-gray-700 m-0 col-span-1">Inserisci Fascia Oraria della Pausa</label>
              {!creatingPause && !pauseTimes.some(pause => !pause.confirmed) ? (
                <button onClick={handleAddPause} className="ml-3 border rounded-md bg-grey-400 py-1 px-2 mr-2 text-center text-sm font-semibold text-gray-700 shadow-md hover:bg-gray-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 flex items-center">
                  <PlusIcon className='w-5 h-5 text-indigo-500 mr-1' />  
                  AGGIUNGI PAUSA
                </button>
              ) : (
                <button onClick={() => { setCreatingPause(false); setPauseTimes(pauseTimes.filter(pause => pause.confirmed)), setBreakTimeError('')}} className="ml-3 border rounded-md bg-white py-1 px-2 mr-2 text-center text-sm font-semibold text-gray-700 shadow-md hover:bg-gray-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 flex items-center">
                  ANNULLA
                </button>
              )}
            </div>
          </div>
          

          {pauseTimes.map((pause, index) => {
            const hasNext = index < pauseTimes.length - 1;
            const isEditing = !pause.confirmed;
            const disableInputs = !workStartTime || !workEndTime || !isEditing;

            return (
              <div key={pause.id ?? index} className="mt-4 w-full">
                <div className="grid grid-cols-12">
                  {/* START */}
                  <div className="col-span-6 sm:col-span-4 flex items-center justify-start">
                    <span className="text-sm font-medium text-gray-700 flex items-center px-4">
                      <LocalBarOutlinedIcon className="!h-[17px] !w-[17px] mr-1" />
                      Dalle ore:
                    </span>

                    <div className="relative inline-block">
                      {isEditing ? (
                        <input
                          type="text"
                          placeholder="hh:mm"
                          value={pause.start_at}
                          onInput={(e) => handlePauseStartTimeChange(index, e)}
                          onBlur={() => handlePauseTimeBlur(index, "start_at")}
                          className="mt-1 p-1 pl-4 border-gray-200 rounded-md focus:border-indigo-500 block w-24 shadow-md sm:text-sm"
                          disabled={disableInputs}
                        />
                      ) : (
                        <span className="mt-1 p-1 pl-4 rounded-md text-left block w-24 shadow-md sm:text-sm">
                          {pause.start_at}
                        </span>
                      )}

                      <ClockIcon className="w-4 h-4 absolute top-1/2 right-2 -translate-y-1/2" />
                    </div>
                  </div>

                  {/* END */}
                  <div className="col-span-6 sm:col-span-4 flex items-center justify-start">
                    <span className="text-sm font-medium text-gray-700 flex items-center px-4">
                      <LocalBarOutlinedIcon className="!h-[17px] !w-[17px] mr-1" />
                      Alle ore:
                    </span>

                    <div className="relative inline-block">
                      {isEditing ? (
                        <input
                          type="text"
                          placeholder="hh:mm"
                          value={pause.end_at}
                          onInput={(e) => handlePauseEndTimeChange(index, e)}
                          onBlur={() => handlePauseTimeBlur(index, "end_at")}
                          className="mt-1 p-1 pl-4 border-gray-200 rounded-md focus:ring-indigo-500 focus:border-indigo-500 block w-24 shadow-md sm:text-sm"
                          disabled={disableInputs}
                        />
                      ) : (
                        <span className="mt-1 p-1 pl-4 text-left rounded-md block w-24 shadow-md sm:text-sm">
                          {pause.end_at}
                        </span>
                      )}

                      <ClockIcon className="w-4 h-4 absolute top-1/2 right-2 -translate-y-1/2" />
                    </div>
                  </div>

                  {/* ACTIONS */}
                  <div className="flex items-center justify-center gap-3 pt-3 col-span-12 sm:col-span-4 flex-wrap">
                    {allowHiddenBreaks && (
                      <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          checked={pause.is_visible ?? true}
                          onChange={() => handleTogglePauseVisible(index)}
                          disabled={false}
                        />
                        <span className="text-xs font-medium uppercase">Visibile</span>
                      </label>
                    )}

                    {isEditing ? (
                      <button
                        onClick={() => handleConfirmPause(index)}
                        className="bg-indigo-600 text-white text-xs px-3 py-1 rounded-md hover:bg-indigo-500 font-semibold uppercase disabled:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                        disabled={disableInputs || !pause.start_at || !pause.end_at}
                      >
                        Aggiungi
                      </button>
                    ) : (
                      <button
                        onClick={() => handleDeletePause(index)}
                        className="text-gray-700 px-1"
                        title="Elimina"
                      >
                        <TrashIcon className="h-6 w-6 hover:text-red-500" />
                      </button>
                    )}
                  </div>
                </div>

                {/* DIVIDER SOLO MOBILE (non tocca i bordi) */}
                {hasNext && (
                  <div className="block sm:hidden mt-4">
                    <div className="border-b border-gray-200 mx-4"></div>
                  </div>
                )}
              </div>
            );
          })}
          <div className='grid grid-cols-2 gap-2 mt-7 mb-4'>
            <div className='col-span-1 flex justify-end'>
              <button onClick={handleConfirmClick} className="uppercase font-semibold text-white text-sm px-4 py-2 rounded-md bg-indigo-600 hover:bg-indigo-500 focus:outline-none focus:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">Conferma Fascia Oraria</button>
            </div>

            <div className="col-span-1 flex justify-start">
              <button onClick={handleCancelClick} className="uppercase bg-gray-200 text-gray-700 text-sm font-semibold px-4 py-2 rounded-md ml-4 hover:bg-gray-300 focus:outline-none focus:bg-gray-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">Annulla</button>
            </div>

          </div>
        </div>
      )}
    </>
  );
}

export default TimeSlot;

